/**
 * LLM 后台「一个数字怎么显示」的**唯一一家**。列表、抽屉、图、看板全问这里。
 *
 * 之前这套规则有四份(`cost-metrics.js` / `call-format.js` / `llm-model-form.js` /
 * `Reconciliation.vue` 内联),同一笔钱在成本页是 `¥1,234.567`、在调用日志里是 `¥1234.567`。
 *
 * 供应商成本的统一单位是人民币 micros；积分单独按整数展示，不走金额格式化。
 */

export const MICROS_PER_YUAN = 1_000_000
const MONEY_DECIMALS = 6

/**
 * 账本单位 → 人民币。小数位数跟着换算率走,不做四舍五入的美化。
 * @param {number} units
 * @returns {string}  `¥0.107` / `¥1,234.567`
 */
export function formatMoney(units) {
  if (!Number.isFinite(units)) throw new Error(`llm-format: 不是数字(收到 ${JSON.stringify(units)})`)
  const yuan = units / MICROS_PER_YUAN
  return `¥${yuan.toLocaleString('en-US', { minimumFractionDigits: MONEY_DECIMALS, maximumFractionDigits: MONEY_DECIMALS })}`
}

/**
 * @param {number} value
 * @returns {string}  `21,522`
 */
export function formatInt(value) {
  if (!Number.isFinite(value)) throw new Error(`llm-format: 不是数字(收到 ${JSON.stringify(value)})`)
  return value.toLocaleString('en-US')
}

/**
 * @param {number|null} value
 * @returns {string}
 */
export function formatOptionalInt(value) {
  return value === null ? '—' : formatInt(value)
}

/**
 * 比率:后端给的是 0~1 的小数。
 * @param {number} rate
 * @returns {string}  `14.3%`
 */
export function formatRate(rate) {
  if (!Number.isFinite(rate)) throw new Error(`llm-format: 不是数字(收到 ${JSON.stringify(rate)})`)
  return `${(rate * 100).toFixed(1)}%`
}

/**
 * 选中的度量怎么显示:成本是钱,其余是计数。
 * @param {'supplier_cost_micros'|'calls'|'charged_points'} metric
 * @param {number} value
 * @returns {string}
 */
export function formatMetric(metric, value) {
  return metric === 'supplier_cost_micros' ? formatMoney(value) : formatInt(value)
}

/**
 * 耗时:不到 1 秒报毫秒,到了就报秒 —— 一列里全是四位数毫秒,人读不出快慢。
 * @param {number} ms
 * @returns {string}  `843ms` / `2.1s`
 */
export function formatDuration(ms) {
  if (!Number.isFinite(ms)) throw new Error(`llm-format: 不是数字(收到 ${JSON.stringify(ms)})`)
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

/**
 * 时刻:定宽,列宽才算得准。**按本地时区**,不是把 ISO 截前 19 个字符。
 * @param {string} iso
 * @returns {string}  `2026-07-13 20:15:31`
 */
export function formatDateTime(iso) {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) throw new Error(`llm-format: 不是时刻(收到 ${JSON.stringify(iso)})`)
  const pad = (n) => String(n).padStart(2, '0')
  const date = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
  return `${date} ${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`
}

/**
 * 一行四个 token 数:prompt / completion / cache read / cache write。NULL 表示未采集。
 * @param {Record<string, number|null>} row
 * @returns {string}
 */
export function formatTokens(row) {
  return [
    row.prompt_tokens,
    row.completion_tokens,
    row.cached_tokens,
    row.cache_write_tokens,
  ].map(formatOptionalInt).join(' / ')
}

/**
 * 路由链上的第几步。**库里的 `attempt_index` 是 0 基的**(第一次尝试是 0),页面上只出现 1 基的
 * 「第 1 步」—— 运营数步数从 1 开始。这条换算原来散在三个组件里,漏了两处,列表和抽屉都在写「第 0 步」。
 *
 * @param {number} attempt_index `route.attempt_index`
 * @returns {number}
 */
export function stepNumber(attempt_index) {
  return attempt_index + 1
}
